import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useApiConfig } from '@/app/ApiProvider';
import { apiFetch } from '@/app/api-fetch';
import { orgRoute } from '@/app/org-route';
import { FileToDataURL } from '@/lib/FileToDataURL';

// SAssetNew — standalone "+ Add asset" form. Mirrors the data the
// onboarding wizard captures so a returning admin can add more assets
// without restarting the wizard. Photo is stored as a data URL on the
// asset row; thumbnail renders on Fleet + Asset Detail.

type Site = { id: string; name: string };
type CreatedAsset = { assetId: string };

const CRITICALITIES = ['low', 'medium', 'high'] as const;
const ASSET_CLASSES = ['Pump', 'Motor', 'Compressor', 'Gearbox', 'Fan', 'Other'];

export function SAssetNew() {
  const apiConfig = useApiConfig();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: async () => {
      const res = await apiFetch(apiConfig, '/v1/sites');
      if (!res.ok) throw new Error('sites unavailable');
      const data = (await res.json()) as { items: Site[] };
      return data.items;
    },
    staleTime: 30_000,
  });

  const [name, setName] = useState('');
  const [assetClass, setAssetClass] = useState(ASSET_CLASSES[0]);
  const [siteName, setSiteName] = useState('');
  const [criticality, setCriticality] = useState<typeof CRITICALITIES[number]>('medium');
  const [photo, setPhoto] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiFetch(apiConfig, '/v1/assets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          assetClass,
          siteName: siteName || sites.data?.[0]?.name || '',
          criticality,
          image_data_url: photo || undefined,
        }),
      });
      if (!res.ok) {
        let msg = 'failed to create asset';
        try {
          const b = (await res.json()) as { error?: string };
          if (b?.error) msg = b.error;
        } catch {
          /* non-JSON */
        }
        throw new Error(msg);
      }
      return (await res.json()) as CreatedAsset;
    },
    onSuccess: (asset) => {
      // Invalidate the Fleet + Asset list queries so the new row
      // appears immediately on the back-navigate.
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['fleet'] });
      navigate({ to: orgRoute(`/assets/${asset.assetId}`) });
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : 'failed'),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    create.mutate();
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-sp-6 px-sp-6 py-sp-9">
      <header className="flex flex-col gap-sp-2">
        <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Fleet
        </p>
        <h1 className="text-fs-h1 font-medium text-gray-900">Add asset</h1>
        <p className="text-fs-body text-gray-700">
          Register a new piece of equipment. Sensors can be assigned after the
          asset is created.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-sp-5 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
      >
        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Name
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Cooling pump 204"
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
          />
        </label>

        <div className="grid grid-cols-2 gap-sp-4">
          <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
            Class
            <select
              value={assetClass}
              onChange={(e) => setAssetClass(e.target.value)}
              className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
            >
              {ASSET_CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
            Criticality
            <select
              value={criticality}
              onChange={(e) => setCriticality(e.target.value as typeof CRITICALITIES[number])}
              className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
            >
              {CRITICALITIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
          Site
          <select
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-2"
          >
            <option value="">{sites.data?.[0]?.name ?? 'No site'}</option>
            {(sites.data ?? []).map((s) => (
              <option key={s.id} value={s.name}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <FileToDataURL
          label="Photo (optional, ≤ 1 MiB)"
          accept="image/png,image/jpeg"
          value={photo}
          onChange={setPhoto}
        />

        {formError ? (
          <p role="alert" className="text-fs-meta text-critical">
            {formError}
          </p>
        ) : null}

        <div className="flex items-center gap-sp-3">
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-r-default bg-info px-sp-4 py-sp-2 text-fs-body font-medium text-gray-50 disabled:opacity-60"
          >
            {create.isPending ? 'Saving…' : 'Add asset'}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: orgRoute('/fleet') })}
            className="rounded-r-default border border-gray-300 px-sp-4 py-sp-2 text-fs-body text-gray-900"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
